// @flow weak

import React, {useState, Fragment} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {isEmpty} from 'lodash';
import {actionIcCheckCircle24px, toggleIcRadioButtonUnchecked24px} from '@ey/motif-react-wrapper/assets/icons/index';
import Icon from '../BaseComponentWrappers/MotifIcon';

// *******************************************************
// TOOL-TIP RAIL
// *******************************************************
export function TooltipRail(props, {activeHandleID: string, getRailProps: GetRailProps, getEventData: GetEventData}) {
	const {activeHandleID, getRailProps, val, isViewBodyOnly, aresSlider, labelValue} = props;
	const [value, setValue] = useState(null),
		[percent, setPercent] = useState(null),
		[isTooltipVisible, setIsToolTipVisible] = useState(false);

	const onMouseEnter = () => {
		document.addEventListener('mousemove', onMouseMove);
		setIsToolTipVisible(true);
	};

	const onMouseLeave = () => {
		setValue(null);
		setPercent(null);
		document.removeEventListener('mousemove', onMouseMove);
		setIsToolTipVisible(false);
	};

	const onMouseMove = (e) => {
		const {activeHandleID, getEventData} = props;

		if (activeHandleID) {
			setValue(null);
			setPercent(null);
		} else {
			const result = getEventData(e);
			let textTip = '';
			if (val && val.length) {
				textTip = result && result.value && typeof result.value === 'number' ? val[result.value - 1] : '';
			}
			setValue(textTip);
			setPercent(result.percent);
		}
	};

	return (
		<Fragment>
			<HandleToolTipWrapper
				className={`HandleToolTipWrapper ${
					labelValue === 5 && percent === 100
						? 'handleToolTipRight'
						: labelValue === 1 && percent === 0
						? 'handleToolTipLeft'
						: ''
				}`}
			>
				{!activeHandleID && value && isTooltipVisible && (aresSlider === false || aresSlider === undefined) ? (
					<div
						className={`tooltipWrapper ${!isViewBodyOnly ? 'ViewOptionsTooltip' : ''}`}
						style={{left: `${percent}%`}}
					>
						<div className="tooltip">
							<span className="tooltiptext">{value}</span>
						</div>
					</div>
				) : null}
				<TooltipWrapper className={`TooltipWrapper ${!isViewBodyOnly ? 'ViewOptionsTooltip' : ''}`}>
					<section
						className="railStyle"
						{...getRailProps({
							onMouseEnter: onMouseEnter,
							onMouseLeave: onMouseLeave
						})}
					/>
					<section className="railCenterStyle" />
				</TooltipWrapper>
			</HandleToolTipWrapper>
		</Fragment>
	);
}

const HandleToolTipWrapper = styled.section`
	.tooltipWrapper {
		.hideToolTip {
			display: none;
		}
		position: absolute;
		top: calc(var(--px-20) * -1);
		width: var(--px-300);
		.tooltip {
			position: relative;
			opacity: 1;
		}
		&.ViewOptionsTooltip {
			.railStyle {
				cursor: default;
			}
		}
		.tooltip .tooltiptext {
			background-color: var(--neutrals-00white);
			color: var(--neutrals-900);
			padding: var(--px-10);
			position: absolute;
			z-index: 1;
			bottom: 150%;
			left: -16%;
			font-size: var(--px-12);
			box-shadow: 0 0 var(--px-8) var(--px-2) rgba(0, 0, 0, 0.15);
			line-height: normal;
			border: var(--px-1) solid var(--neutrals-300);
			min-width: var(--px-100);
			max-width: var(--px-300);
			text-align: center;
		}

		&::after {
			content: '';
			position: absolute;
			bottom: calc(var(--px-6) * -1);
			@media only screen and (min-width: 1280px) and (max-width: 1366px) {
				bottom: calc(var(--px-4) * -1);
			}
			left: calc(var(--px-5) * -1);
			border: var(--px-1) solid var(--neutrals-300);
			border-top-width: 0;
			border-left-width: 0;
			transform: rotate(45deg);
			background: var(--neutrals-00white);
			width: var(--px-10);
			height: var(--px-10);
			z-index: 9999999999;
		}
	}
	&.handleToolTipRight {
		.tooltipWrapper {
			.tooltip .tooltiptext {
				left: -21%;
			}
			&::after {
				left: calc(var(--px-15) * -1);
			}
		}
	}
	&.handleToolTipLeft {
		.tooltipWrapper {
			.tooltip .tooltiptext {
				left: -14%;
			}
			&::after {
				left: var(--px-3);
			}
		}
	}
`;

const TooltipWrapper = styled.section`
	&.ViewOptionsTooltip {
		.railStyle {
			cursor: default !important;
		}
	}
	.railStyle {
		position: absolute;
		width: 100%;
		height: var(--px-3);
		transform: translate(0, -50%);
		cursor: pointer;
		background-color: var(--neutrals-300);
		z-index: 99;
	}
	.railCenterStyle {
		position: absolute;
		width: 100%;
		height: var(--px-1);
		transform: translate(0, -50%);
		pointer-events: none;
		background-color: var(--neutrals-300);
	}
`;

// ********************************************************
// SLIDER RAIL (No tool-tips)
// ********************************************************
export function SliderRail({getRailProps}) {
	return (
		<Fragment>
			<SliderRailWrapper className="SliderRailWrapper">
				<section className="railOuterStyle" {...getRailProps()} />
				<section className="railInnerStyle" />
			</SliderRailWrapper>
		</Fragment>
	);
}

SliderRail.propTypes = {
	getRailProps: PropTypes.func.isRequired
};

const SliderRailWrapper = styled.section`
	.railOuterStyle {
		position: absolute;
		width: 100%;
		height: var(--px-1);
		transform: translate(0, -50%);
		cursor: pointer;
	}
	.railInnerStyle {
		position: absolute;
		width: 100%;
		height: var(--px-1);
		transform: translate(0, -50%);
		pointer-events: none;
		background-color: var(--neutrals-300);
	}
`;

// *********************************************************
// HANDLE COMPONENT
// *********************************************************
export function Handle(props) {
	const {
		domain: [min, max],
		handle: {id, value, percent},
		val,
		isActive,
		disabled,
		getHandleProps,
		isViewBodyOnly
	} = props;

	const [mouseOver, setMouseOver] = useState(false);

	const onMouseEnter = () => {
		setMouseOver(true);
	};

	const onMouseLeave = () => {
		setMouseOver(false);
	};

	return (
		<Fragment>
			{(mouseOver || isActive) && !disabled && !isEmpty(val) ? (
				<HandleToolTipWrapper className="HandleToolTipWrapper">
					{!props.aresSlider ? (
						<div
							className={`tooltipWrapper ${!isViewBodyOnly ? 'ViewOptionsTooltip' : ''}`}
							style={{left: `${percent}%`}}
						>
							<div className="tooltip">
								<span className="tooltiptext">{val}</span>
							</div>
						</div>
					) : (
						<></>
					)}
					<TooltipWrapper className={`TooltipWrapper ${!isViewBodyOnly ? 'ViewOptionsTooltip' : ''}`}>
						<section
							className="railStyle"
							{...getHandleProps(id, {
								onMouseEnter: onMouseEnter,
								onMouseLeave: onMouseLeave
							})}
						/>
						<section className="railCenterStyle" />
					</TooltipWrapper>
				</HandleToolTipWrapper>
			) : null}

			<HandleWrapper className="HandleWrapper">
				{!isViewBodyOnly ? (
					<>
						<section
							className="handleContainer"
							style={{left: `${percent > 0 ? (percent === 100 ? '98' : percent) : '1.5'}%`}}
							{...getHandleProps(id, {
								onMouseEnter: onMouseEnter,
								onMouseLeave: onMouseLeave
							})}
						>
							<section
								className="handleSlider"
								role="slider"
								aria-valuemin={min}
								aria-valuemax={max}
								aria-valuenow={value}
							/>
						</section>
					</>
				) : (
					<section
						aria-valuemin={min}
						aria-valuemax={max}
						aria-valuenow={value}
						style={{left: `${percent > 0 ? (percent === 100 ? '97.8' : percent) : '1.5'}%`}}
						className={`ViewOptions ViewSlider active ${disabled ? 'DefaultSliders' : ''} ${
							percent > 0 ? (percent === 100 ? 'lastTicks' : percent) : 'FirstTicks'
						}`}
					>
						<Icon src={!disabled ? actionIcCheckCircle24px : toggleIcRadioButtonUnchecked24px} />
					</section>
				)}
			</HandleWrapper>
		</Fragment>
	);
}

Handle.propTypes = {
	domain: PropTypes.array.isRequired,
	handle: PropTypes.shape({
		id: PropTypes.string.isRequired,
		value: PropTypes.number.isRequired,
		percent: PropTypes.number.isRequired
	}).isRequired,
	getHandleProps: PropTypes.func.isRequired,
	disabled: PropTypes.bool
};

Handle.defaultProps = {
	disabled: false
};

const HandleWrapper = styled.section`
	.handleContainer {
		position: absolute;
		transform: translate(-50%, -50%);
		webkit-tap-highlight-color: var(--neutrals-1000);
		z-index: 101;
		width: var(--px-20);
		height: var(--px-20);
		cursor: pointer;
		background: var(--neutrals-00white);
	}
	.handleSlider {
		position: absolute;
		transform: translate(-50%, -50%);
		z-index: 100;
		width: var(--px-20);
		height: var(--px-20);
		border-radius: 50%;
		background-color: var(--neutrals-900);
		top: var(--px-10);
		left: var(--px-10);
	}
	.ViewSlider {
		position: absolute;
		top: calc(var(--px-12) * -1);
		margin-left: calc(var(--px-10) * -1);
		z-index: 999;
		background-color: var(--neutrals-00white);
		@media only screen and (min-width: 1280px) and (max-width: 1366px) {
			top: calc(var(--px-10) * -1);
		}
		.motif-icon {
			margin-right: 0 !important;
			width: var(--px-20);
			height: var(--px-20);
		}
		&.DefaultSliders {
			.motif-icon {
				svg {
					fill: var(--neutrals-300) !important;
					cursor: default !important;
				}
			}
		}
	}
`;

// *******************************************************
// KEYBOARD HANDLE COMPONENT
// Uses a button to allow keyboard events
// *******************************************************
export function KeyboardHandle({domain: [min, max], handle: {id, value, percent}, disabled, getHandleProps}) {
	return (
		<KeyboardHandleWrapper
			role="slider"
			aria-valuemin={min}
			aria-valuemax={max}
			aria-valuenow={value}
			style={{left: `${percent}%`}}
			{...getHandleProps(id)}
		/>
	);
}

KeyboardHandle.propTypes = {
	domain: PropTypes.array.isRequired,
	handle: PropTypes.shape({
		id: PropTypes.string.isRequired,
		value: PropTypes.number.isRequired,
		percent: PropTypes.number.isRequired
	}).isRequired,
	getHandleProps: PropTypes.func.isRequired,
	disabled: PropTypes.bool
};

KeyboardHandle.defaultProps = {
	disabled: false
};

const KeyboardHandleWrapper = styled.button`
	position: absolute;
	transform: translate(-50%, -50%);
	z-index: 2;
	width: var(--px-24);
	height: var(--px-24);
	border-radius: 50%;
	box-shadow: var(--px-1) var(--px-1) var(--px-1) var(--px-1) rgba(0, 0, 0, 0.3);
	background-color: var(--neutrals-300);
`;

// *******************************************************
// TRACK COMPONENT
// *******************************************************
export function Track({source, target, getTrackProps, disabled}) {
	return (
		<TrackHandleWrapper
			style={{left: `${source.percent}%`, width: `${target.percent - source.percent}%`}}
			{...getTrackProps()}
		/>
	);
}

Track.propTypes = {
	source: PropTypes.shape({
		id: PropTypes.string.isRequired,
		value: PropTypes.number.isRequired,
		percent: PropTypes.number.isRequired
	}).isRequired,
	target: PropTypes.shape({
		id: PropTypes.string.isRequired,
		value: PropTypes.number.isRequired,
		percent: PropTypes.number.isRequired
	}).isRequired,
	getTrackProps: PropTypes.func.isRequired,
	disabled: PropTypes.bool
};

Track.defaultProps = {
	disabled: false
};

const TrackHandleWrapper = styled.button`
	position: absolute;
	transform: translate(0%, -50%);
	height: var(--px-1);
	z-index: 1;
	background-color: var(--neutrals-300);
	cursor: pointer;
`;

// *******************************************************
// TICK COMPONENT
// *******************************************************
export function Tick({tick, count, format}) {
	return <Fragment></Fragment>;
}

Tick.propTypes = {
	tick: PropTypes.shape({
		id: PropTypes.string.isRequired,
		value: PropTypes.string.isRequired,
		percent: PropTypes.string.isRequired
	}).isRequired,
	count: PropTypes.string.isRequired,
	format: PropTypes.func.isRequired
};

Tick.defaultProps = {
	format: (d) => d
};
